import { useCallback, useEffect, useMemo, useState } from 'react';
import { actionClick, useUiEvent } from '@lingoda/analytics';
import type { FormProps } from '@lingoda/forms';
import { Alert, Box, Hidden, SvgIcon, Typography, useResolution } from '@lingoda/ui';
import { withForm } from '@lingoda/forms';
import { getTranslatedSection, trans } from '@lingoda/i18n';
import { styled } from '@lingoda/ui/theme';
import { type SectionName } from '@lingoda/sections';
import { useToggleState } from '@lingoda/hooks';
import { useCreatePackagePurchaseMutation } from '@lingoda/monolith-graphql';
import { getCheckoutPath, goTo, useLocation } from '@lingoda/router';
import { showToast } from '@lingoda/toasts';
import { getQueryParams } from '@lingoda/urls';
import { Grid } from '@lingoda/ui/layout/GridNext';
import { GiftIcon } from '@lingoda/ui/icons/lingodaIcons';
import { useGetLifetimeDiscountCoupon } from '@lingoda/subscriptions';
import { SectionIcons } from '@lingoda/ui/icons/SectionIcons';
import { Filters, getRecommendedUpgradePackage, useStoreContext } from '../../components';
import useResponsivePackageLayout from './useResponsivePackageLayout';
import TrialBanner from './TrialBanner';
import type { FormValues } from './types';
import type { PackageId, PurchasePackage } from '../../types';

interface OwnProps {
    sectionName: SectionName;
    isTrialAvailable?: boolean;
    showTrialBanner?: boolean;
    isLearningLanguageChange?: boolean;
    onCreatePackage?: ({ purchaseId }: { purchaseId: string }) => void;
}

type Props = OwnProps & FormProps<FormValues>;

const FullWidthItem = styled(Grid)({
    width: '100%',
});

const StoreContent = ({
    sectionName,
    values: { purchasePackageId },
    setFieldValue,
    onCreatePackage,
    isTrialAvailable = false,
    showTrialBanner = true,
    isLearningLanguageChange,
}: Props) => {
    const addEvent = useUiEvent();
    const isTablet = useResolution('tablet');

    const [selectedPackage, setSelectedPackage] = useState<PurchasePackage | undefined>();
    const [isUserSelection, setIsUserSelection, setIsDefaultSelection] = useToggleState(false);
    const { filteredPackages, filtersOptions, filtersState, updateFilter } = useStoreContext();

    const hasMixedCredits = useMemo(() => {
        return filteredPackages.some((pkg) => pkg.groupCredits > 0 && pkg.privateCredits > 0);
    }, [filteredPackages]);

    const { search } = useLocation();

    const { coupon, specialType } = getQueryParams<{
        coupon: string;
        specialType: string;
    }>(search);

    const [createPackagePurchase] = useCreatePackagePurchaseMutation({
        onCompleted: ({ purchase }) => {
            void addEvent(actionClick('InternalStorePurchase'), {
                metadata: {
                    purchaseId: purchase.id,
                },
            });
            const checkoutPath = getCheckoutPath(purchase);
            const couponString = coupon?.toString();
            const params = couponString
                ? { purchaseId: purchase.id, couponCode: couponString }
                : { purchaseId: purchase.id };
            if (onCreatePackage) {
                onCreatePackage(params);
            } else {
                goTo(checkoutPath(params));
            }
        },
        onError: (error) => showToast('error', error.message),
    });

    const showCurriculumFilter =
        filtersOptions.sectionCurriculums.length > 1 && !isTablet && !isLearningLanguageChange;

    const setUserPackage = useCallback(
        (pkg?: PurchasePackage) => {
            setSelectedPackage(pkg);
            void setFieldValue('purchasePackageId', pkg?.id || null);
        },
        [setSelectedPackage, setFieldValue],
    );

    const handlePackageSelect = (id: PackageId | undefined) => {
        const userSelectedPackage = filteredPackages.find((pkg) => pkg.id === id);
        const canSelectPackage = userSelectedPackage && !userSelectedPackage.isCurrent;
        setUserPackage(canSelectPackage ? userSelectedPackage : undefined);
        setIsUserSelection();
    };

    const handlePackageDeselect = () => {
        handlePackageSelect(undefined);
    };

    const onCheckout = ({ trial = false }: { trial?: boolean } = {}) => {
        if (purchasePackageId && filtersState?.currency) {
            const options = isLearningLanguageChange ? { newSectionName: sectionName } : null;
            void createPackagePurchase({
                variables: {
                    packageId: purchasePackageId,
                    currency: filtersState?.currency,
                    withTrial: isTrialAvailable && trial,
                    options,
                },
            });
        }
    };

    useEffect(() => {
        if (filteredPackages.length === 0) {
            setIsDefaultSelection();
            setUserPackage();

            return;
        }

        const currentPackage = filteredPackages.find((pkg) => pkg.id === purchasePackageId);
        if (currentPackage) {
            setUserPackage(currentPackage);
        } else {
            const specialTypeString = specialType?.toString();
            const firstVisiblePackage = getRecommendedUpgradePackage(
                filteredPackages,
                specialTypeString,
            );
            setIsDefaultSelection();
            setUserPackage(firstVisiblePackage);
        }
    }, [filteredPackages, setUserPackage, setIsDefaultSelection, purchasePackageId, specialType]);

    const { Layout, Item } = useResponsivePackageLayout();

    const data = useGetLifetimeDiscountCoupon();
    const hasLifetimeDiscount = !data.loading && !!data.couponCode;

    return (
        <Grid container justifyContent="space-between" alignItems="center" spacing={3}>
            {isTrialAvailable && showTrialBanner && (
                <Grid xs={12}>
                    <TrialBanner />
                </Grid>
            )}
            <Grid
                container
                direction={{ xs: 'row', md: showCurriculumFilter ? 'column' : 'row' }}
                spacing={3}
                xs={12}
                justifyContent="space-between"
            >
                <Grid xs={12} sm="auto">
                    <Typography variant="h2" noWrap>
                        {trans('title', {}, 'student-store')}
                    </Typography>
                    <Hidden mdUp={showCurriculumFilter}>
                        <Box display="flex">
                            <Box fontSize={24} mr={1} textAlign="center">
                                <SvgIcon fontSize="inherit" component={SectionIcons[sectionName]} />
                            </Box>
                            <Typography
                                variant="h3"
                                noWrap
                                textTransform="capitalize"
                                htmlColor="text.secondary"
                            >
                                {getTranslatedSection(sectionName)}
                            </Typography>
                        </Box>
                    </Hidden>
                </Grid>
                <Grid xs={12} sm="auto">
                    <Filters
                        options={filtersOptions}
                        filtersState={filtersState}
                        onFilterUpdate={updateFilter}
                        isLearningLanguageChange={isLearningLanguageChange}
                        hasMixedCredits={hasMixedCredits}
                    />
                </Grid>
            </Grid>
            <Grid xs={12}>
                <Layout
                    selectedPackage={selectedPackage}
                    onPackageDeselect={handlePackageDeselect}
                    isUserSelection={isUserSelection}
                    onCheckout={onCheckout}
                    isTrialAvailable={isTrialAvailable}
                    isLearningLanguageChange={isLearningLanguageChange}
                    footerMessage={
                        filteredPackages.length > 0
                            ? trans('price-rounded-to-49-or-99-cents', {}, 'student-store')
                            : undefined
                    }
                >
                    {filteredPackages.length > 0 ? (
                        <Grid container spacing={2}>
                            {hasLifetimeDiscount && (
                                <FullWidthItem key="lifetime-discount-alert">
                                    <Alert
                                        icon={<GiftIcon size={24} />}
                                        severity="promotion"
                                        size="compact"
                                        variant="outlined"
                                        title={trans(
                                            'your-lifetime-discount-will-be-automatically-applied-at-checkout',
                                            {},
                                            'public-payment',
                                        )}
                                    />
                                </FullWidthItem>
                            )}
                            {filteredPackages.map((pkg) => (
                                <FullWidthItem key={pkg.id}>
                                    <Item
                                        pkg={pkg}
                                        isSelected={pkg.id === selectedPackage?.id}
                                        onPackageSelect={handlePackageSelect}
                                        onCheckout={onCheckout}
                                        isTrialAvailable={isTrialAvailable}
                                    />
                                </FullWidthItem>
                            ))}
                        </Grid>
                    ) : (
                        <Typography variant="h3" textAlign="center">
                            {trans('no-packages-found', {}, 'student-store')}
                        </Typography>
                    )}
                </Layout>
            </Grid>
        </Grid>
    );
};

export default withForm<OwnProps, FormValues>({
    mapPropsToValues: () => {
        const formValues = {
            purchasePackageId: null,
        };

        return formValues as FormValues;
    },
    handleSubmit: (values, form) => {
        console.log('submitted', values, form);
    },
})(StoreContent);
